/**
 * Shared alarm utilities for the site dashboard alarms grid card.
 *
 * Clearability logic and category resolution used by AlarmsSummaryCard —
 * split out so the rules read independently of the grid's render code.
 */

/**
 * Returns true when an alarm state row is eligible for the Clear action.
 *
 * Clearable by definition, OR marked excessive, OR staff can clear
 * relay_no_current alarms (superuser proxy).
 *
 * @param {object}  def       - alarm definition from the reference catalog (may be {})
 * @param {object}  opts
 * @param {boolean} opts.excessive  - alarm state's `excessive` flag
 * @param {boolean} opts.isStaff    - capabilities.is_staff
 * @param {string}  opts.categoryKey - resolved top-level category key (e.g. 'relay_no_current')
 * @returns {boolean}
 */
export function isAlarmClearable(def, { excessive, isStaff, categoryKey }) {
  if (def?.is_clearable) return true;
  if (excessive) return true;
  if (isStaff && categoryKey === 'relay_no_current') return true;
  return false;
}

/**
 * Walks up the category tree to the top-level (root) category for an alarm.
 *
 * The reference catalog has a two-level tree: leaf categories have a
 * parent_id pointing at a root category. This function returns the root-level
 * category object so the grid card's chip label matches the category's
 * top-level key.
 *
 * @param {Map<number, object>} catById - alarm category map keyed by id
 * @param {number|null}         catId   - category_id from the alarm definition
 * @returns {object|null} root category object, or null when not found
 */
export function resolveTopCategory(catById, catId) {
  if (catId == null) return null;
  const cat = catById.get(catId) ?? null;
  if (!cat) return null;
  const parentCatId = cat.parent_id ?? null;
  return parentCatId != null ? (catById.get(parentCatId) ?? cat) : cat;
}
